/**
 * Retoure & Umtausch, Stufe 1 – reine Rechenlogik (ENTWURF, nicht freigegeben).
 *
 * Grundlage: docs/retoure/02-fachkonzept.md Abschnitt 6. Dieses Modul bucht, zahlt und signiert
 * nichts; es rechnet nur aus den gespeicherten Positionen eines Originalbelegs, welche Zeilen
 * rückgabefähig sind und welcher Betrag je Zeile und Steuersatz zu erstatten wäre. Es hat
 * bewusst keine Importe, damit die Kasse (Vorschau) und später der Server (verbindliche
 * Rechnung) dieselbe Datei nutzen und nie auseinanderlaufen.
 *
 * Regeln (Fachkonzept 6.1/6.2):
 * 1. Grundlage sind nur die Werte des Originalbelegs (Preis nach Zeilenrabatt, Bon-Rabattzeilen,
 *    Steuersatz je Position), nie der aktuelle Artikelstamm.
 * 2. Effektiver Zeilenbetrag `E = B + Rabattanteil`: Die Bon-Rabattzeilen („Rabatt n %“, auch
 *    Kundenrabatt) eines Steuersatzes werden auf die Artikel- und freien Zeilen dieses Satzes mit
 *    positivem Betrag verteilt – anteilig nach `B`, Verfahren des größten Rests, bei gleichem Rest
 *    die frühere Zeile zuerst (wie `verteileBonRabatt` in verkaufsanalyse.ts). Pfand und Leergut
 *    bekommen keinen Anteil (wie beim Rabatt selbst). Die Summe aller `E` eines Satzes ist exakt
 *    die Summe dieses Satzes am Beleg (ohne Pfand/Leergut/Gutscheine).
 * 3. Kumulativer Betrag `K(k) = runde(E × k / M)`, `K(0) = 0`, `K(M) = E`. Erstattung für `q` Stück,
 *    wenn schon `q0` zurück sind: `K(q0 + q) − K(q0)`. Damit nimmt die letzte Teilretoure den
 *    Rundungsrest auf, und die Summe aller Teilretouren einer Zeile ist genau `E`.
 * 4. Rundung: kaufmännisch, ab ,5 vom Nullpunkt weg, in Ganzzahl-Arithmetik (BigInt, keine
 *    Gleitkommazahl im Geldpfad, Regel 5).
 * 5. Steuersatz der Rückgabezeile = Satz der Originalzeile, auch 19 % und 4,9 % (beide im
 *    RKSV-Feld „Besonders“; getrennt ausgewiesen, weil aus dem Feld nicht ableitbar).
 * 6. Netto/USt je Satz wie im Kern (`nettoUndUst`, Promille, Vorzeichen symmetrisch).
 *
 * Nicht rückgabefähig (ausdrücklich gesperrt bzw. markiert, Fachkonzept B6 und Auftrag):
 * Rabattzeilen (gehen über `E` ein), Pfand (E-6/RF-9 offen), Leergut, Wertgutschein-Verkauf
 * (Bonverkauf), Einzweck-Gutschein-Verkauf und -Einlösung (eigener Gutscheinprozess, RF-6),
 * negative Zeilen (Rücknahmen, freie Abzüge). Mengen, die keine ganze Zahl sind (Gewicht), sind
 * nur vollständig rückgebbar.
 */

export type RetoureSteuersatz = '20' | '10' | '13' | '0' | '19' | '4,9';

const PROMILLE: Readonly<Record<RetoureSteuersatz, number>> = {
  '20': 200,
  '10': 100,
  '13': 130,
  '0': 0,
  '19': 190,
  '4,9': 49,
};

export function istRetoureSteuersatz(x: unknown): x is RetoureSteuersatz {
  return typeof x === 'string' && Object.hasOwn(PROMILLE, x);
}

/** RKSV-Betragsfelder des maschinenlesbaren Codes (Anlage Z 4). */
export type RksvFeld = 'normal' | 'ermaessigt1' | 'ermaessigt2' | 'nullsatz' | 'besonders';

/** Feld eines Steuersatzes; 19 % und 4,9 % teilen sich „Besonders“. */
export function rksvFeld(satz: RetoureSteuersatz): RksvFeld {
  switch (satz) {
    case '20':
      return 'normal';
    case '10':
      return 'ermaessigt1';
    case '13':
      return 'ermaessigt2';
    case '0':
      return 'nullsatz';
    case '19':
    case '4,9':
      return 'besonders';
  }
}

/** Gespeicherte Position des Originalbelegs (Spiegel von `positionen` in `rksv.beleg`). */
export interface OriginalPosition {
  bezeichnung: string;
  menge: number;
  bruttoCent: number;
  steuersatz: string;
  artikelId?: number | null | undefined;
  /** Wiegeposition (Menge in kg): immer nur vollständig rückgebbar, auch bei 2,000 kg. */
  einheit?: 'kg' | null | undefined;
}

export type PositionsArt =
  | 'artikel'
  | 'frei'
  | 'rabatt'
  | 'pfand'
  | 'leergut'
  | 'gutscheinverkauf'
  | 'einzweckgutschein'
  | 'gutscheineinloesung'
  | 'ruecknahme';

/** Präfixe wie in der Kasse (rabatt.ts, pfand.ts) und im Server (verkaufsanalyse.ts, gutscheine.ts). */
const RABATT_PRAEFIX = 'Rabatt ';
const PFAND_PRAEFIX = 'Pfand ';
const LEERGUT_PRAEFIX = 'Leergut ';
const EINZWECK_VERKAUF_PRAEFIX = 'Gutschein ';
const BONVERKAUF = /^Wertgutschein .+ \(Bonverkauf, kein Umsatz\)$/;
const EINZWECK_EINLOESUNG = /^Einzweck-Gutschein .+ \(bereits versteuert\)$/;

/**
 * Art einer Originalposition. Reihenfolge wie `klassifiziere` (verkaufsanalyse.ts):
 * Wertgutschein-Verkauf, Einzweck-Einlösung, Pfand/Leergut (nach Bezeichnung, auch mit Artikel),
 * Artikel, Rabatt, Einzweck-Verkauf, sonst frei. Negative Artikel- bzw. freie Zeilen sind
 * Rücknahmen bzw. Abzüge.
 */
export function positionsArt(p: OriginalPosition): PositionsArt {
  if (p.steuersatz === '0' && BONVERKAUF.test(p.bezeichnung)) {
    return 'gutscheinverkauf';
  }
  if (EINZWECK_EINLOESUNG.test(p.bezeichnung)) {
    return 'gutscheineinloesung';
  }
  if (p.bezeichnung.startsWith(LEERGUT_PRAEFIX)) {
    return 'leergut';
  }
  if (p.bezeichnung.startsWith(PFAND_PRAEFIX)) {
    return p.bruttoCent < 0 ? 'leergut' : 'pfand';
  }
  if (p.artikelId !== undefined && p.artikelId !== null) {
    return p.bruttoCent < 0 ? 'ruecknahme' : 'artikel';
  }
  if (p.bezeichnung.startsWith(RABATT_PRAEFIX) && p.bruttoCent < 0) {
    return 'rabatt';
  }
  if (p.bezeichnung.startsWith(EINZWECK_VERKAUF_PRAEFIX)) {
    return 'einzweckgutschein';
  }
  return p.bruttoCent < 0 ? 'ruecknahme' : 'frei';
}

/** Begründung, warum eine Zeile nicht zurückgegeben werden kann (null = rückgabefähig). */
export const SPERRGRUND: Readonly<Record<PositionsArt, string | null>> = {
  artikel: null,
  frei: null,
  rabatt: 'Rabattzeile – anteilig in den Artikelbeträgen enthalten',
  pfand: 'Pfand – in Stufe 1 gesperrt (Entscheidung E-6 / Rechtsfrage RF-9 offen)',
  leergut: 'Leergut – war bereits eine Rücknahme',
  gutscheinverkauf: 'Wertgutschein – Erstattung nur über einen eigenen Gutscheinprozess (RF-6)',
  einzweckgutschein:
    'Einzweck-Gutschein – Erstattung nur über einen eigenen Gutscheinprozess (RF-6)',
  gutscheineinloesung: 'Gutscheineinlösung – nicht rückgabefähig',
  ruecknahme: 'Rücknahme bzw. Abzug im Original – nicht rückgabefähig',
};

// ---------------------------------------------------------------------------
// Ganzzahlige Rundung
// ---------------------------------------------------------------------------

/** runde(zaehler / nenner), kaufmännisch (ab ,5 vom Nullpunkt weg); nenner > 0. */
export function rundeQuotient(zaehler: bigint, nenner: bigint): bigint {
  if (nenner <= 0n) {
    throw new RangeError('Nenner muss positiv sein');
  }
  const betrag = zaehler < 0n ? -zaehler : zaehler;
  const gerundet = (2n * betrag + nenner) / (2n * nenner);
  return zaehler < 0n ? -gerundet : gerundet;
}

function alsZahl(wert: bigint): number {
  const n = Number(wert);
  if (!Number.isSafeInteger(n)) {
    throw new RangeError('Betrag außerhalb des sicheren Ganzzahlbereichs');
  }
  return n;
}

/**
 * Netto und enthaltene USt eines Bruttobetrags – dieselbe Rechnung wie `nettoUndUst` im Kern
 * (round(|Brutto| × 1000 / (1000 + Promille)), Vorzeichen danach), hier ganzzahlig.
 */
export function nettoUndUstRetoure(
  bruttoCent: number,
  satz: RetoureSteuersatz,
): { nettoCent: number; ustCent: number } {
  if (!Number.isSafeInteger(bruttoCent)) {
    throw new TypeError('bruttoCent muss ganzzahlig sein');
  }
  const betrag = alsZahl(
    rundeQuotient(BigInt(Math.abs(bruttoCent)) * 1000n, BigInt(1000 + PROMILLE[satz])),
  );
  const nettoCent = bruttoCent < 0 ? 0 - betrag : betrag;
  return { nettoCent, ustCent: bruttoCent - nettoCent };
}

// ---------------------------------------------------------------------------
// Effektive Zeilenbeträge
// ---------------------------------------------------------------------------

export interface Zeilenwert {
  /** Index im `positionen`-Array des Originals (stabil, das Original ist unveränderlich). */
  index: number;
  art: PositionsArt;
  bezeichnung: string;
  menge: number;
  /** Gespeicherter Zeilenbetrag `B` (nach Zeilenrabatt bzw. neuem Preis). */
  bruttoCent: number;
  steuersatz: RetoureSteuersatz;
  artikelId: number | null;
  /** Anteil der Bon-Rabattzeilen dieses Satzes (≤ 0); 0 bei nicht rückgabefähigen Zeilen. */
  rabattAnteilCent: number;
  /** `E = B + Rabattanteil` (nur bei rückgabefähigen Zeilen aussagekräftig). */
  effektivCent: number;
  /** null = rückgabefähig, sonst der Sperrgrund. */
  sperrgrund: string | null;
  /** Menge ist keine ganze Zahl (z. B. Gewicht): nur vollständig rückgebbar. */
  nurGanz: boolean;
}

export interface Zeilenauswertung {
  zeilen: Zeilenwert[];
  /** Strukturfehler, die eine Berechnung verhindern (Klärungsfall); leer = in Ordnung. */
  fehler: string[];
}

/**
 * Effektive Zeilenbeträge aller Positionen eines Originalbelegs. Bei einem Strukturfehler
 * (unbekannter Satz, ungültige Menge, nicht zuordenbarer Rabatt) wird nichts erstattet –
 * lieber Klärung als eine falsche Auszahlung.
 */
export function effektiveZeilen(positionen: readonly OriginalPosition[]): Zeilenauswertung {
  const fehler: string[] = [];
  const zeilen: Zeilenwert[] = positionen.map((p, index) => {
    const nr = String(index + 1);
    if (!istRetoureSteuersatz(p.steuersatz)) {
      fehler.push(`Position ${nr}: unbekannter Steuersatz ${p.steuersatz}`);
    }
    if (!Number.isSafeInteger(p.bruttoCent)) {
      fehler.push(`Position ${nr}: Betrag ist keine ganze Centzahl`);
    }
    if (!Number.isFinite(p.menge) || p.menge <= 0) {
      fehler.push(`Position ${nr}: Menge muss größer 0 sein`);
    } else if (Number.isInteger(p.menge) && !Number.isSafeInteger(p.menge)) {
      // A3: Das API-Schema begrenzt Mengen nicht; jenseits von 2^53 würden Restmengen still
      // gerundet (Mengendrift über mehrere Teilretouren).
      fehler.push(`Position ${nr}: Menge außerhalb des sicheren Ganzzahlbereichs`);
    }
    const art = positionsArt(p);
    return {
      index,
      art,
      bezeichnung: p.bezeichnung,
      menge: p.menge,
      bruttoCent: p.bruttoCent,
      steuersatz: istRetoureSteuersatz(p.steuersatz) ? p.steuersatz : '0',
      artikelId: p.artikelId ?? null,
      rabattAnteilCent: 0,
      effektivCent: p.bruttoCent,
      sperrgrund: SPERRGRUND[art],
      // Wiegeartikel: Teilmengen-Retoure in kg ist nicht freigegeben – auch ganzzahlige kg
      // (2,000 kg) dürfen nicht stückweise (1 von 2) zurückgegeben werden.
      nurGanz: !Number.isInteger(p.menge) || p.einheit === 'kg',
    };
  });
  if (fehler.length > 0) {
    return { zeilen, fehler };
  }
  // A3: Gewichte und Belegsumme werden als Zahl summiert – nur exakt, solange die Summe der
  // Beträge im sicheren Ganzzahlbereich bleibt.
  if (!Number.isSafeInteger(zeilen.reduce((s, z) => s + Math.abs(z.bruttoCent), 0))) {
    fehler.push('Summe der Positionsbeträge außerhalb des sicheren Ganzzahlbereichs');
    return { zeilen, fehler };
  }
  const saetze = new Set(zeilen.filter((z) => z.art === 'rabatt').map((z) => z.steuersatz));
  for (const satz of saetze) {
    const rabattSumme = zeilen
      .filter((z) => z.art === 'rabatt' && z.steuersatz === satz)
      .reduce((s, z) => s + z.bruttoCent, 0);
    const empfaenger = zeilen.filter(
      (z) => z.steuersatz === satz && (z.art === 'artikel' || z.art === 'frei') && z.bruttoCent > 0,
    );
    const gewicht = empfaenger.reduce((s, z) => s + z.bruttoCent, 0);
    const betrag = -rabattSumme;
    if (gewicht === 0) {
      fehler.push(`Rabatt zu ${satz} % ohne Artikelzeile dieses Satzes – nicht zuordenbar`);
      continue;
    }
    if (betrag > gewicht) {
      fehler.push(`Rabatt zu ${satz} % ist höher als der Warenwert dieses Satzes`);
      continue;
    }
    const g = BigInt(gewicht);
    const teile = empfaenger.map((z) => {
      const produkt = BigInt(betrag) * BigInt(z.bruttoCent);
      return { z, anteil: produkt / g, rest: produkt % g };
    });
    let uebrig = BigInt(betrag) - teile.reduce((s, t) => s + t.anteil, 0n);
    const reihenfolge = [...teile].sort((a, b) =>
      a.rest === b.rest ? a.z.index - b.z.index : a.rest > b.rest ? -1 : 1,
    );
    for (const t of reihenfolge) {
      if (uebrig <= 0n) {
        break;
      }
      t.anteil += 1n;
      uebrig -= 1n;
    }
    for (const t of teile) {
      t.z.rabattAnteilCent = -alsZahl(t.anteil);
      t.z.effektivCent = t.z.bruttoCent + t.z.rabattAnteilCent;
    }
  }
  return { zeilen, fehler };
}

/** Kumulativer Betrag `K(k)` für k von M Stück einer Zeile mit effektivem Betrag E. */
export function kumulativerBetrag(effektivCent: number, menge: number, k: number): number {
  if (k === 0) {
    return 0;
  }
  if (k === menge) {
    return effektivCent;
  }
  if (!Number.isInteger(menge) || !Number.isInteger(k) || k < 0 || k > menge) {
    throw new RangeError('Teilmengen nur bei ganzzahliger Menge zwischen 0 und M');
  }
  return alsZahl(rundeQuotient(BigInt(effektivCent) * BigInt(k), BigInt(menge)));
}

// ---------------------------------------------------------------------------
// Sperren und Vorgeschichte (Codex-Prüfung Retoure, M1 und M3)
// ---------------------------------------------------------------------------

/** Negative Zeilen, die ein Original in Stufe 1 vollständig sperren (Rabattzeilen nicht). */
const SPERRENDE_NEGATIVE: readonly PositionsArt[] = [
  'leergut',
  'ruecknahme',
  'gutscheineinloesung',
];

export const NEGATIVE_ZEILEN_SPERRE =
  'Original enthält negative Zeilen (Leergut, Rücknahme, Abzug oder Gutscheineinlösung) – Rückgabe in Stufe 1 gesperrt, weil sonst die Erstattung über mehrere Retouren die Belegsumme übersteigen könnte (fachlich ungeklärt, Klärungsfall)';

/**
 * M1: Ein Original mit Leergut, Rücknahmen, freien Abzügen oder Gutscheineinlösungen ist in
 * Stufe 1 ganz gesperrt. Solche Zeilen mindern die Belegsumme, ohne einer Artikelzeile
 * zugeordnet zu sein; jede Artikelzeile für sich bliebe erstattbar, zusammen aber mehr als
 * gezahlt wurde. Bon-Rabattzeilen sperren nicht – sie sind über `E` zugeordnet.
 */
export function negativeZeilenSperre(zeilen: readonly Pick<Zeilenwert, 'art'>[]): string[] {
  return zeilen.some((z) => SPERRENDE_NEGATIVE.includes(z.art)) ? [NEGATIVE_ZEILEN_SPERRE] : [];
}

/**
 * M3: Vorgeschichte (bereits zurückgegebene Mengen je Index) strukturiert prüfen, statt beim
 * Rechnen eine Ausnahme zu werfen: Position vorhanden und rückgabefähig, 0 ≤ q0 ≤ M, bei
 * ganzzahliger Menge ganzzahlig, bei Gewichtszeilen nur 0 oder vollständig.
 */
export function pruefeVorgeschichte(
  werte: readonly Zeilenwert[],
  bisherZurueck: ReadonlyMap<number, number>,
): string[] {
  const fehler: string[] = [];
  for (const [index, q0] of bisherZurueck) {
    const w = Number.isInteger(index) ? werte[index] : undefined;
    if (w === undefined) {
      fehler.push(
        `Vorgeschichte nennt Position ${String(index + 1)}, die es im Originalbeleg nicht gibt`,
      );
      continue;
    }
    const nr = `Position ${String(index + 1)} (${w.bezeichnung})`;
    if (!Number.isFinite(q0) || q0 < 0 || q0 > w.menge) {
      fehler.push(`${nr}: bisher zurückgegebene Menge ist ungültig`);
    } else if (q0 > 0 && w.sperrgrund !== null) {
      fehler.push(`${nr}: Vorgeschichte für eine nicht rückgabefähige Position`);
    } else if (w.nurGanz && q0 !== 0 && q0 !== w.menge) {
      fehler.push(`${nr}: bisher zurückgegebene Menge bei Gewichtszeile nur 0 oder vollständig`);
    } else if (!w.nurGanz && !Number.isInteger(q0)) {
      fehler.push(`${nr}: bisher zurückgegebene Menge muss eine ganze Zahl sein`);
    }
  }
  return fehler;
}

function euroText(cent: number): string {
  const betrag = Math.abs(cent);
  const text = `${String(Math.floor(betrag / 100))},${String(betrag % 100).padStart(2, '0')} €`;
  return cent < 0 ? `-${text}` : text;
}

/** Kumulative Prüfung auf bereits geprüften Zeilenwerten (bisher + jetzt ≤ Belegsumme). */
function belegsummeUeberschritten(
  werte: readonly Zeilenwert[],
  bisherZurueck: ReadonlyMap<number, number>,
  summeCent: number,
): string | null {
  let bisherCent = 0;
  for (const [index, q0] of bisherZurueck) {
    const w = werte[index];
    if (w?.sperrgrund === null) {
      bisherCent += kumulativerBetrag(w.effektivCent, w.menge, q0);
    }
  }
  const belegsumme = werte.reduce((s, w) => s + w.bruttoCent, 0);
  return bisherCent + summeCent > belegsumme
    ? `Erstattung insgesamt über der Belegsumme: bisher ${euroText(bisherCent)} + jetzt ${euroText(summeCent)} > Belegsumme ${euroText(belegsumme)} – Klärungsfall`
    : null;
}

/**
 * M1: Erstattungen eines Originals kumulativ begrenzen – alle bisherigen Rückgaben (K(q0) je
 * Zeile) plus die neue Erstattung dürfen die Belegsumme nie übersteigen. Ungültige
 * Vorgeschichte oder Strukturfehler ergeben ebenfalls einen Fehlertext (keine Ausnahme).
 */
export function ueberschreitetBelegsumme(
  positionen: readonly OriginalPosition[],
  bisherZurueck: ReadonlyMap<number, number>,
  summeCent: number,
): string | null {
  const { zeilen: werte, fehler } = effektiveZeilen(positionen);
  const alle = [...fehler, ...(fehler.length > 0 ? [] : pruefeVorgeschichte(werte, bisherZurueck))];
  if (alle.length > 0) {
    return alle.join('; ');
  }
  return belegsummeUeberschritten(werte, bisherZurueck, summeCent);
}

// ---------------------------------------------------------------------------
// Erstattung einer Auswahl
// ---------------------------------------------------------------------------

export interface RetoureAuswahl {
  /** Index der Originalposition. */
  index: number;
  /** Zurückgegebene Menge in dieser Retoure. */
  menge: number;
}

export interface ErstattungsZeile {
  index: number;
  bezeichnung: string;
  steuersatz: RetoureSteuersatz;
  artikelId: number | null;
  menge: number;
  /** Bereits zurückgegebene Menge vor dieser Retoure (q0). */
  bisherZurueck: number;
  /** Erstattung dieser Zeile (positiv, Cent) – `K(q0 + q) − K(q0)`. */
  erstattungCent: number;
  /** Derselbe Anteil ohne Bon-Rabatt (Anzeige „Originalpreis“). */
  ohneRabattCent: number;
  /** Anteiliger Bon-Rabatt (≤ 0): erstattungCent − ohneRabattCent. */
  rabattAnteilCent: number;
}

export interface SteuersatzSumme {
  steuersatz: RetoureSteuersatz;
  bruttoCent: number;
  nettoCent: number;
  ustCent: number;
}

export type ErstattungsErgebnis =
  | {
      ok: true;
      zeilen: ErstattungsZeile[];
      /** Erstattung je Steuersatz (positiv), in fester Reihenfolge 20, 10, 13, 0, 19, 4,9. */
      jeSteuersatz: SteuersatzSumme[];
      /**
       * Beträge, die ein Retourenbeleg je RKSV-Feld trüge (negativ) – nur Vorschau. Belegtyp
       * und Darstellung sind offen (RF-1, RF-4); signiert wird ausschließlich im Kern.
       */
      rksvFelder: Record<RksvFeld, number>;
      summeCent: number;
    }
  | { ok: false; fehler: string[] };

const SATZ_REIHENFOLGE: readonly RetoureSteuersatz[] = ['20', '10', '13', '0', '19', '4,9'];

/**
 * Erstattung für eine Auswahl von Positionen und Mengen. `bisherZurueck` enthält je Index die
 * bereits zurückgegebene Menge (q0) aus abgeschlossenen und reservierten Retouren; fehlt ein
 * Index, gilt 0. Prüft: Position vorhanden und rückgabefähig, Menge > 0 und ganzzahlig (außer
 * vollständige Rückgabe einer Gewichtszeile), Menge ≤ Restmenge, jede Position höchstens einmal,
 * bisherige plus neue Erstattung ≤ Belegsumme (kumulativ, M1), Original ohne sperrende negative
 * Zeilen (M1), gültige Vorgeschichte (M3). Jeder Verstoß verhindert das Ergebnis vollständig;
 * es wird nie eine Ausnahme geworfen.
 */
export function berechneErstattung(
  positionen: readonly OriginalPosition[],
  auswahl: readonly RetoureAuswahl[],
  bisherZurueck: ReadonlyMap<number, number> = new Map(),
): ErstattungsErgebnis {
  const { zeilen: werte, fehler } = effektiveZeilen(positionen);
  if (fehler.length > 0) {
    return { ok: false, fehler };
  }
  const vorab = [...negativeZeilenSperre(werte), ...pruefeVorgeschichte(werte, bisherZurueck)];
  if (vorab.length > 0) {
    return { ok: false, fehler: vorab };
  }
  if (auswahl.length === 0) {
    return { ok: false, fehler: ['Keine Position ausgewählt'] };
  }
  const gesehen = new Set<number>();
  const zeilen: ErstattungsZeile[] = [];
  for (const a of auswahl) {
    const w = Number.isInteger(a.index) ? werte[a.index] : undefined;
    if (w === undefined) {
      fehler.push(`Position ${String(a.index + 1)} gibt es im Originalbeleg nicht`);
      continue;
    }
    const nr = `Position ${String(a.index + 1)} (${w.bezeichnung})`;
    if (gesehen.has(a.index)) {
      fehler.push(`${nr}: doppelt ausgewählt`);
      continue;
    }
    gesehen.add(a.index);
    if (w.sperrgrund !== null) {
      fehler.push(`${nr}: ${w.sperrgrund}`);
      continue;
    }
    // q0 ist durch pruefeVorgeschichte gültig (ganzzahlig bzw. 0/M bei Gewicht)
    const q0 = bisherZurueck.get(a.index) ?? 0;
    const rest = w.menge - q0;
    if (!Number.isFinite(a.menge) || a.menge <= 0) {
      fehler.push(`${nr}: Menge muss größer 0 sein`);
      continue;
    }
    if (rest <= 0) {
      fehler.push(`${nr}: bereits vollständig zurückgegeben`);
      continue;
    }
    if (w.nurGanz) {
      if (q0 !== 0 || a.menge !== w.menge) {
        fehler.push(`${nr}: Menge ist keine ganze Zahl – nur vollständig rückgebbar`);
        continue;
      }
    } else if (!Number.isInteger(a.menge)) {
      fehler.push(`${nr}: Menge muss eine ganze Zahl sein`);
      continue;
    }
    if (a.menge > rest) {
      fehler.push(`${nr}: höchstens ${String(rest)} möglich`);
      continue;
    }
    const bis = q0 + a.menge;
    const erstattungCent =
      kumulativerBetrag(w.effektivCent, w.menge, bis) -
      kumulativerBetrag(w.effektivCent, w.menge, q0);
    const ohneRabattCent =
      kumulativerBetrag(w.bruttoCent, w.menge, bis) - kumulativerBetrag(w.bruttoCent, w.menge, q0);
    zeilen.push({
      index: w.index,
      bezeichnung: w.bezeichnung,
      steuersatz: w.steuersatz,
      artikelId: w.artikelId,
      menge: a.menge,
      bisherZurueck: q0,
      erstattungCent,
      ohneRabattCent,
      rabattAnteilCent: erstattungCent - ohneRabattCent,
    });
  }
  if (fehler.length > 0) {
    return { ok: false, fehler };
  }
  const summeCent = zeilen.reduce((s, z) => s + z.erstattungCent, 0);
  const ueber = belegsummeUeberschritten(werte, bisherZurueck, summeCent);
  if (ueber !== null) {
    return { ok: false, fehler: [ueber] };
  }
  const jeSatz = new Map<RetoureSteuersatz, number>();
  for (const z of zeilen) {
    jeSatz.set(z.steuersatz, (jeSatz.get(z.steuersatz) ?? 0) + z.erstattungCent);
  }
  const jeSteuersatz: SteuersatzSumme[] = SATZ_REIHENFOLGE.filter((s) => jeSatz.has(s)).map(
    (steuersatz) => {
      const bruttoCent = jeSatz.get(steuersatz) ?? 0;
      return { steuersatz, bruttoCent, ...nettoUndUstRetoure(bruttoCent, steuersatz) };
    },
  );
  const rksvFelder: Record<RksvFeld, number> = {
    normal: 0,
    ermaessigt1: 0,
    ermaessigt2: 0,
    nullsatz: 0,
    besonders: 0,
  };
  for (const s of jeSteuersatz) {
    rksvFelder[rksvFeld(s.steuersatz)] -= s.bruttoCent;
  }
  return { ok: true, zeilen, jeSteuersatz, rksvFelder, summeCent };
}

// ---------------------------------------------------------------------------
// Originalbeleg prüfen
// ---------------------------------------------------------------------------

export interface OriginalKopf {
  belegtyp: string;
  sonderbeleg: string | null;
  zahlungsart: 'bar' | 'karte' | 'gutschein' | null;
  /** Mit Mehrzweck-Gutscheinen bezahlter Teil (Teilzahlung). */
  gutscheinCent: number;
  /** Zu diesem Original gibt es bereits einen Stornobeleg (`ST-<Nummer>…`). */
  storniert: boolean;
}

export interface OriginalPruefung {
  /** Darf in Stufe 1 überhaupt als Original einer Rückgabe dienen? */
  rueckgabefaehig: boolean;
  /** Gründe, warum nicht (leer = rückgabefähig). */
  gruende: string[];
}

/**
 * Stufe 1 (Entscheidungsvorlage, Design RT-FINAL): nur Verkaufsbelege (STANDARD, kein
 * Sonderbeleg), nur Barerstattung – mit Karte oder Gutschein bezahlte Belege sind gesperrt
 * (RT-F1, RF-6), ebenso stornierte Belege (RU-0: ein Storno je Original, Gegenbuchung).
 */
export function pruefeOriginal(kopf: OriginalKopf): OriginalPruefung {
  const gruende: string[] = [];
  if (kopf.belegtyp !== 'STANDARD' || kopf.sonderbeleg !== null) {
    gruende.push(
      kopf.belegtyp === 'TRAINING'
        ? 'Trainingsbeleg – kein Original für eine Rückgabe'
        : kopf.belegtyp === 'STORNO'
          ? 'Stornobeleg – kein Original für eine Rückgabe'
          : 'Kein Verkaufsbeleg – kein Original für eine Rückgabe',
    );
  }
  if (kopf.storniert) {
    gruende.push('Beleg ist bereits storniert – nichts mehr zurückzugeben');
  }
  if (kopf.zahlungsart === 'karte') {
    gruende.push('Mit Karte bezahlt – Kartenerstattung ist eine spätere Ausbaustufe (RT-F1)');
  } else if (kopf.zahlungsart === 'gutschein' || kopf.gutscheinCent > 0) {
    gruende.push('Mit Gutschein bezahlt – Erstattungsweg offen (RF-6)');
  } else if (kopf.zahlungsart === null && gruende.length === 0) {
    gruende.push('Beleg ohne Zahlungsart – kein Original für eine Rückgabe');
  }
  return { rueckgabefaehig: gruende.length === 0, gruende };
}

/** Nummer eines Stornos von `original` nach dem Kassenschema `ST-<original>[-<Ziffern>]`. */
export function istStornonummerVon(original: string, belegnummer: string): boolean {
  if (!belegnummer.startsWith(`ST-${original}`)) {
    return false;
  }
  const rest = belegnummer.slice(3 + original.length);
  return rest === '' || /^-\d+$/.test(rest);
}
